// Admin Communications page — lists every email vinoga's ESP (Brevo) has
// touched. Combines transactional sends we initiate (password reset, …)
// and automation-driven sends inserted on first webhook event.
//
// Filterable by status / template / channel + searchable by recipient or
// subject (matched server-side via ILIKE). Paginated, 50 per page.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Mail,
  MessageCircle,
  Search,
  RefreshCw,
  Send,
  CheckCircle2,
  Eye,
  MousePointerClick,
  XCircle,
  AlertCircle,
  Clock,
  Inbox,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

interface Communication {
  id: string;
  order_id: string | null;
  customer_id: string | null;
  channel: "email" | "sms";
  provider: string | null;
  provider_message_id: string | null;
  template: string;
  recipient: string;
  subject: string | null;
  locale: string | null;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string | null;
}

interface ListResponse {
  communications: Communication[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const STATUS_ICON: Record<string, JSX.Element> = {
  queued: <Clock size={12} className="text-muted-foreground" />,
  sent: <Send size={12} className="text-blue-500" />,
  delivered: <CheckCircle2 size={12} className="text-emerald-500" />,
  opened: <Eye size={12} className="text-purple-500" />,
  clicked: <MousePointerClick size={12} className="text-indigo-500" />,
  bounced: <XCircle size={12} className="text-orange-500" />,
  failed: <AlertCircle size={12} className="text-destructive" />,
  unsubscribed: <XCircle size={12} className="text-gray-400" />,
  spam: <AlertCircle size={12} className="text-red-500" />,
};

const STATUS_COLOR: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  sent: "bg-blue-100 text-blue-800",
  delivered: "bg-emerald-100 text-emerald-800",
  opened: "bg-purple-100 text-purple-800",
  clicked: "bg-indigo-100 text-indigo-800",
  bounced: "bg-orange-100 text-orange-800",
  failed: "bg-red-100 text-red-800",
  unsubscribed: "bg-gray-100 text-gray-600",
  spam: "bg-red-100 text-red-900",
};

const STATUSES = [
  "queued",
  "sent",
  "delivered",
  "opened",
  "clicked",
  "bounced",
  "failed",
  "unsubscribed",
  "spam",
] as const;

const TEMPLATES = ["password_reset", "automation", "unknown"] as const;

export function Communications() {
  const navigate = useNavigate();
  const t = useT();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTemplate, setFilterTemplate] = useState("all");
  const [filterChannel, setFilterChannel] = useState("all");

  const queryKey = [
    "admin",
    "communications",
    { page, search, filterStatus, filterTemplate, filterChannel },
  ];

  const { data, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: "50" });
      if (search) params.set("search", search);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterTemplate !== "all") params.set("template", filterTemplate);
      if (filterChannel !== "all") params.set("channel", filterChannel);
      return api<ListResponse>(`/api/v1/admin/communications?${params}`);
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const rows = data?.communications ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchDraft.trim());
  }

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Mail size={20} /> {t("communications.title", "Communications")}
          </h1>
          <p className="mt-0.5 font-body text-xs text-muted-foreground">
            {t(
              "communications.subtitle",
              "All transactional and automation emails sent via Brevo.",
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw
            size={14}
            className={`mr-1 ${isFetching ? "animate-spin" : ""}`}
          />
          {t("communications.refresh", "Refresh")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form
          onSubmit={applySearch}
          className="relative min-w-[200px] max-w-[280px] flex-1"
        >
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder={t(
              "communications.searchPlaceholder",
              "Search by email or subject…",
            )}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            className="h-9 pl-8 font-body text-sm"
          />
        </form>

        <Select
          value={filterTemplate}
          onValueChange={(v) => {
            setFilterTemplate(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[170px] font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("communications.allTemplates", "All templates")}
            </SelectItem>
            {TEMPLATES.map((tpl) => (
              <SelectItem key={tpl} value={tpl} className="capitalize">
                {tpl.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterStatus}
          onValueChange={(v) => {
            setFilterStatus(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[140px] font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("communications.allStatuses", "All statuses")}
            </SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filterChannel}
          onValueChange={(v) => {
            setFilterChannel(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[130px] font-body text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("communications.allChannels", "All channels")}
            </SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-auto shrink-0 font-body text-xs text-muted-foreground">
          {t("communications.totalCount", "{n} total").replace(
            "{n}",
            String(total),
          )}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isFetching && rows.length === 0 ? (
          <div className="p-8 text-center font-body text-sm text-muted-foreground">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin" />
            {t("communications.loading", "Loading…")}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center">
            <Inbox size={32} className="mx-auto mb-3 text-muted-foreground" />
            <p className="font-body text-sm text-muted-foreground">
              {t("communications.empty", "No communications found.")}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-bold">
                  {t("communications.colTemplate", "Template")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colRecipient", "Recipient")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colSubject", "Subject")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colChannel", "Channel")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colStatus", "Status")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colLocale", "Locale")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colSent", "Sent")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("communications.colOrder", "Order")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className="hover:bg-muted/30">
                  <TableCell className="font-body text-sm font-medium capitalize text-foreground">
                    {c.template.replace(/_/g, " ")}
                  </TableCell>
                  <TableCell className="font-body text-xs text-muted-foreground">
                    {c.recipient}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate font-body text-sm text-foreground">
                    {c.subject ?? "—"}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 font-body text-xs capitalize text-muted-foreground">
                      {c.channel === "sms" ? (
                        <MessageCircle size={12} />
                      ) : (
                        <Mail size={12} />
                      )}
                      {c.channel}
                      {c.provider && (
                        <span className="text-[10px] opacity-60">
                          via {c.provider}
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-body text-[10px] font-medium capitalize ${
                        STATUS_COLOR[c.status] ?? "bg-muted text-muted-foreground"
                      }`}
                    >
                      {STATUS_ICON[c.status] ?? null}
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell className="font-body text-xs uppercase text-muted-foreground">
                    {c.locale ?? "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-body text-xs text-muted-foreground">
                    {new Date(c.created_at).toLocaleString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                  <TableCell>
                    {c.order_id ? (
                      <button
                        onClick={() => navigate(`/orders/${c.order_id}`)}
                        className="font-body text-xs text-primary hover:underline"
                      >
                        {t("communications.viewOrder", "View order")}
                      </button>
                    ) : (
                      <span className="font-body text-xs text-muted-foreground">
                        —
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            {t("communications.prev", "Previous")}
          </Button>
          <span className="font-body text-xs text-muted-foreground">
            {t("communications.pageOf", "Page {p} of {n}")
              .replace("{p}", String(page))
              .replace("{n}", String(totalPages))}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            {t("communications.next", "Next")}
          </Button>
        </div>
      )}
    </div>
  );
}

export default Communications;
